import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import {
  ApiError,
  get,
  post,
  type Comment,
  type Me,
  type Member,
  type Revision,
  type Trip,
} from "../../lib/api.ts";
import { canSeeApprove } from "../../lib/rights.ts";
import { useApi } from "../../lib/useApi.ts";
import { formatOffset, parseOffset } from "../../lib/timeline.ts";

/**
 * A version, watched where it is discussed.
 *
 * "Tighten the first 10 seconds" is useless as text and obvious at 0:08, so the version plays in
 * the page, a comment's time seeks the player, and a new comment takes wherever the playhead is.
 * When the proxy is not built yet, or this browser cannot decode it, that is a not-yet rather
 * than a fault: one plain sentence and the original to download.
 *
 * It has its own player rather than reusing MediaViewer/MediaComments: those address a media
 * asset by `assetId` and cannot address a revision, which is a different grant reference and a
 * different comments route. The shared pieces (formatOffset, canSeeApprove) are reused.
 *
 * Mount it with `key={revision.id}` so a version switch discards the draft, playhead and comments
 * of the version you were watching.
 */
export function RevisionReview({
  trip,
  me,
  revision,
  taskOwnerId,
  allowDecisions = true,
  onChanged,
}: {
  trip: Trip;
  me: Me;
  revision: Revision;
  /** The task's owner when the project comes from a task; they and the admin approve. */
  taskOwnerId: string | null;
  /**
   * Whether this surface offers a decision at all. The server is still the authority — this only
   * decides whether the buttons are on the page. The editor's board passes false: accepting a cut
   * is not something an editor does from the screen they uploaded it on, whatever role they hold.
   */
  allowDecisions?: boolean;
  onChanged: () => Promise<void>;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [unplayable, setUnplayable] = useState<"processing" | "browser" | "failed" | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [body, setBody] = useState("");
  const [playhead, setPlayhead] = useState(0);
  const [at, setAt] = useState("");
  const [error, setError] = useState<string | null>(null);
  /** The request-changes message, and whether it is being written. */
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  // Names for the ids on the comments. A failed read costs the names, not the conversation.
  const members = useApi<{ items: Member[] }>(`/v1/trips/${trip.id}/members`);
  const who = (id: string) =>
    members.data?.items.find((m) => m.userId === id)?.displayName ?? "Someone";
  const when = (iso: string) => new Date(iso).toLocaleString();
  /** One re-grant per failure, and where to carry on from once the fresh URL has loaded. */
  const retried = useRef(false);
  const lastRegrant = useRef(0);
  const resumeAt = useRef(0);
  const tripId = trip.id;
  const revisionId = revision.id;
  const base = `/v1/trips/${tripId}/revisions/${revisionId}`;
  const grants = `/v1/trips/${tripId}/media-grants`;

  const canDecide =
    allowDecisions &&
    canSeeApprove({
      roles: trip.roles,
      userId: me.user.id,
      taskOwnerId,
      revision: { authorId: revision.createdBy },
    });

  // One grant per version, not one per render.
  useEffect(() => {
    let live = true;
    setSrc(null);
    setUnplayable(null);
    post<{ url: string }>(`/v1/trips/${tripId}/media-grants`, {
      variant: "PREVIEW",
      reference: { kind: "REVISION", revisionId },
    })
      .then((g) => {
        if (live) setSrc(g.url);
      })
      .catch((e: Error) => {
        if (!live) return;
        // The proxy is not built yet: a not-yet, never an error state.
        if (e instanceof ApiError && e.status === 409 && e.code === "STATE_CONFLICT") {
          setUnplayable("processing");
        } else {
          // Any other failure still leaves the reader unable to play it: same escape hatch.
          setUnplayable("failed");
        }
      });
    return () => {
      live = false;
    };
  }, [tripId, revisionId]);

  const load = useCallback(async () => {
    return (await get<{ items: Comment[] }>(`${base}/comments`)).items;
  }, [base]);
  useEffect(() => {
    let live = true;
    load()
      .then((items) => {
        if (live) setComments(items);
      })
      .catch((e: Error) => {
        if (live) setError(e.message);
      });
    return () => {
      live = false;
    };
  }, [load]);

  /** The moment a new comment attaches to: null at the very start, so caption and payload agree. */
  const playheadMs = (): number | null => {
    const t = video.current?.currentTime;
    if (t === undefined || !Number.isFinite(t)) return null;
    const ms = Math.round(t * 1000);
    return ms > 0 ? ms : null;
  };
  const readPlayhead = () => setPlayhead(playheadMs() ?? 0);

  // A grant is a link that expires (ten minutes), so a long review session finds the player
  // failing on a video that played a minute ago. Ask once more for a fresh link and carry on from
  // where it was; only a second failure means the browser really cannot play it.
  async function onVideoError() {
    // Spent until video actually plays, and never twice within 30s, so a proxy whose headers
    // parse but whose frames fail cannot loop on the grant endpoint.
    if (retried.current || Date.now() - lastRegrant.current < 30_000) {
      return setUnplayable("browser");
    }
    retried.current = true;
    lastRegrant.current = Date.now();
    resumeAt.current = video.current?.currentTime ?? 0;
    try {
      const g = await post<{ url: string }>(grants, {
        variant: "PREVIEW",
        reference: { kind: "REVISION", revisionId },
      });
      setSrc(g.url);
    } catch {
      setUnplayable("browser");
    }
  }

  function seek(ms: number) {
    const el = video.current;
    if (!el) return;
    el.currentTime = ms / 1000;
    setPlayhead(ms);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    // With a player the playhead decides; without one the reader types where they are.
    const offsetMs = unplayable ? parseOffset(at) : playheadMs();
    try {
      await post(`${base}/comments`, { body, ...(offsetMs !== null ? { offsetMs } : {}) });
      setBody("");
      setAt("");
      setComments(await load());
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function decide(decision: "APPROVE" | "REQUEST_CHANGES", reason?: string) {
    setError(null);
    try {
      await post(`${base}/review-decisions`, {
        decision,
        expectedSha256: revision.sha256,
        ...(reason?.trim() ? { reason: reason.trim() } : {}),
      });
      setAsking(false);
      setReason("");
      await onChanged();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function download() {
    setError(null);
    try {
      const g = await post<{ url: string }>(grants, {
        variant: "ORIGINAL",
        download: true,
        reference: { kind: "REVISION", revisionId },
      });
      window.open(g.url, "_blank", "noopener");
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div className="stack panel">
      <div className="row wrap">
        <strong className="grow">Version {revision.versionNumber}</strong>
        {!unplayable && (
          <button type="button" className="button" onClick={() => void download()}>
            Download
          </button>
        )}
      </div>
      {unplayable ? (
        <div className="stack">
          <p className="muted">
            {unplayable === "processing"
              ? "This version is still being processed, so it cannot be played yet."
              : unplayable === "failed"
                ? "This version could not be loaded for playing right now."
                : "This version cannot be played in this browser."}{" "}
            You can download the original instead.
          </p>
          <div className="row">
            <button type="button" className="button" onClick={() => void download()}>
              Download the original
            </button>
          </div>
        </div>
      ) : (
        src && (
          <video
            ref={video}
            className="review-player"
            src={src}
            controls
            playsInline
            preload="metadata"
            aria-label={`Version ${revision.versionNumber}`}
            onError={() => void onVideoError()}
            onPlaying={() => {
              // Video really came out of the element: a later failure is a new expiry.
              retried.current = false;
            }}
            onLoadedMetadata={(e) => {
              if (resumeAt.current > 0) e.currentTarget.currentTime = resumeAt.current;
              resumeAt.current = 0;
            }}
            onTimeUpdate={readPlayhead}
            onSeeked={readPlayhead}
            onPause={readPlayhead}
          />
        )
      )}
      {revision.notes && <p>{revision.notes}</p>}
      {revision.decisions.length > 0 && (
        <ul className="plain">
          {revision.decisions.map((d) => (
            <li key={d.id} className="muted">
              {d.decision === "APPROVE" ? "Approved" : "Changes requested"} ·{" "}
              {new Date(d.at).toLocaleString()}
              {d.reason ? ` — ${d.reason}` : ""}
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {canDecide && revision.isCurrent && !asking && (
        <div className="row wrap">
          <button className="button primary" onClick={() => void decide("APPROVE")}>
            Approve this version
          </button>
          <button className="button" onClick={() => setAsking(true)}>
            Request changes
          </button>
        </div>
      )}
      {/* Asking for changes without saying what to change leaves the editor guessing, so the
          button opens this rather than sending anything. The server refuses a blank one too. */}
      {canDecide && revision.isCurrent && asking && (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            void decide("REQUEST_CHANGES", reason);
          }}
        >
          <label className="stack">
            <span>What needs changing?</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={2000}
              required
              autoFocus
              placeholder="Tighten the opening, and hold the last shot a beat longer."
            />
          </label>
          <div className="row wrap">
            <button className="button primary" disabled={!reason.trim()}>
              Send request
            </button>
            <button
              type="button"
              className="button"
              onClick={() => {
                setAsking(false);
                setReason("");
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
      {canDecide && !revision.isCurrent && (
        <p className="muted">A newer version exists, so this one can no longer be approved.</p>
      )}
      <h4>Comments</h4>
      {comments.length === 0 ? (
        <p className="muted">No comments on this version.</p>
      ) : (
        <ul className="plain">
          {comments.map((c) => (
            <li key={c.id} className="row wrap">
              {c.offsetMs !== null &&
                (unplayable ? (
                  <span className="pill">at {formatOffset(c.offsetMs)}</span>
                ) : (
                  <button
                    type="button"
                    className="pill seek"
                    onClick={() => seek(c.offsetMs!)}
                    aria-label={`play from ${formatOffset(c.offsetMs)}`}
                  >
                    {formatOffset(c.offsetMs)}
                  </button>
                ))}
              <span className="grow">
                {c.body}
                {/* Who said it and when: a note on a cut is a conversation, and a line of it
                    with no name against it is of no use to whoever reads it next. */}
                <span className="small muted comment-by">
                  {who(c.authorId)} · <time dateTime={c.createdAt}>{when(c.createdAt)}</time>
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={submit} className="stack">
        <div className="row wrap">
          <input
            className="grow"
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
              readPlayhead();
            }}
            placeholder="Add a comment on this version"
            maxLength={8000}
            required
            aria-label="Comment"
          />
          <button className="button">Comment</button>
        </div>
        {unplayable ? (
          <input
            className="narrow"
            value={at}
            onChange={(e) => setAt(e.target.value)}
            placeholder="m:ss"
            pattern="\d+(:[0-5]?\d){0,2}"
            aria-label="Timestamp (minutes:seconds, optional)"
            title="Optional: where in the video this is about, like 1:23"
          />
        ) : (
          <p className="small muted">
            {playhead > 0
              ? `Will be attached to ${formatOffset(playhead)} in the video.`
              : "Not tied to a moment. Play or scrub the video to pick one."}
          </p>
        )}
      </form>
    </div>
  );
}
